import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext } from 'react';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextLink from '@ui/atoms/TextLink';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import spaced from '@ui/styled/Spaced';

import useMemberInfo from '@hooks/useMemberInfo';

import { interpolate, keysToCamel } from '@utils/butter';
import { getStateName } from '@utils/intake/stateRequirements';
import { getFlowName } from '@utils/service-availability';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowBailoutStepProps } from '@folx-types/flow-steps';

interface IMemberStateIneligibilityStepProps extends IFlowBailoutStepProps {}

const SpacedGridItem = spaced(CenteredGridItem);

const MemberStateIneligibilityStep: FC<IMemberStateIneligibilityStepProps> = () => {
	const context = useContext(FlowContext);
	const { destinationFlow: flowId } = context;

	const { loading, memberInfo } = useMemberInfo();

	interface IOnboardingStateIneligibilityContent {
		header: string;
		body: string;
		folxLink: string;
		folxHref: string;
		helpLink: string;
		helpHref: string;
	}

	const { pageContent } = useStaticQuery(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "member-state-ineligibility" }) {
					header
					body
					folx_link
					folx_href
					help_link
					help_href
				}
			}
		`
	);

	const refinedContent: IOnboardingStateIneligibilityContent = keysToCamel(
		pageContent
	) as IOnboardingStateIneligibilityContent;
	const { header, body, folxLink, folxHref, helpLink, helpHref } = refinedContent;

	return (
		<LoadingWrapper loading={loading} skeleton={<></>}>
			<SpacedGridItem marginBottom="xxl">
				<FlexColumn gap="sm">
					<SubheadingText>
						{interpolate(header, {
							state: getStateName(memberInfo?.stateOfResidence?.abbreviation ?? ''),
							service: getFlowName(flowId),
						})}
					</SubheadingText>
					<BasicText>{body}</BasicText>
					<FlexColumn gap="xssm">
						<TextLink label={folxLink} href={folxHref} />
						<TextLink label={helpLink} href={helpHref} newTab />
					</FlexColumn>
				</FlexColumn>
			</SpacedGridItem>
		</LoadingWrapper>
	);
};

export default MemberStateIneligibilityStep;
